interface ScrollVisibilityOptions {
  viewportTopMargin?: number;
  viewportBottomMargin?: number;
  viewportLeftMargin?: number;
  viewportRightMargin?: number;
  ancestorMargin?: number;
}

interface ScrollableAncestor {
  element: HTMLElement;
  canScrollY: boolean;
  canScrollX: boolean;
}

const DEFAULT_ANCESTOR_MARGIN = 12;

function hasScrollableOverflow(value: string): boolean {
  return value === "auto" || value === "scroll" || value === "overlay";
}

function getScrollableAncestors(el: HTMLElement): ScrollableAncestor[] {
  const ancestors: ScrollableAncestor[] = [];
  let current = el.parentElement;

  while (current && current !== document.body) {
    const style = getComputedStyle(current);
    const overflowY = style.overflowY || style.overflow;
    const overflowX = style.overflowX || style.overflow;
    const canScrollY =
      hasScrollableOverflow(overflowY) &&
      current.scrollHeight > current.clientHeight + 1;
    const canScrollX =
      hasScrollableOverflow(overflowX) &&
      current.scrollWidth > current.clientWidth + 1;

    if (canScrollY || canScrollX) {
      ancestors.push({ element: current, canScrollY, canScrollX });
    }

    current = current.parentElement;
  }

  return ancestors;
}

export function targetNeedsScrollIntoView(
  el: HTMLElement,
  options: ScrollVisibilityOptions = {},
): boolean {
  const {
    viewportTopMargin = 0,
    viewportBottomMargin = 0,
    viewportLeftMargin = 0,
    viewportRightMargin = 0,
    ancestorMargin = DEFAULT_ANCESTOR_MARGIN,
  } = options;

  const rect = el.getBoundingClientRect();
  if (
    rect.top < viewportTopMargin ||
    rect.bottom > window.innerHeight - viewportBottomMargin ||
    rect.left < viewportLeftMargin ||
    rect.right > window.innerWidth - viewportRightMargin
  ) {
    return true;
  }

  for (const ancestor of getScrollableAncestors(el)) {
    const ancestorRect = ancestor.element.getBoundingClientRect();
    if (ancestorRect.width <= 0 || ancestorRect.height <= 0) {
      continue;
    }

    if (
      (ancestor.canScrollY &&
        (rect.top < ancestorRect.top + ancestorMargin ||
          rect.bottom > ancestorRect.bottom - ancestorMargin)) ||
      (ancestor.canScrollX &&
        (rect.left < ancestorRect.left + ancestorMargin ||
          rect.right > ancestorRect.right - ancestorMargin))
    ) {
      return true;
    }
  }

  return false;
}

export function scrollTargetIntoView(
  el: HTMLElement,
  options: ScrollVisibilityOptions & { behavior?: ScrollBehavior } = {},
): void {
  const {
    viewportTopMargin = 0,
    viewportBottomMargin = 0,
    viewportLeftMargin = 0,
    viewportRightMargin = 0,
    ancestorMargin = DEFAULT_ANCESTOR_MARGIN,
    behavior = "auto",
  } = options;

  for (const ancestor of getScrollableAncestors(el)) {
    const rect = el.getBoundingClientRect();
    const ancestorRect = ancestor.element.getBoundingClientRect();
    const nextTop = ancestor.canScrollY
      ? getNextScrollTop(ancestor.element, rect, ancestorRect, ancestorMargin)
      : null;
    const nextLeft = ancestor.canScrollX
      ? getNextScrollLeft(ancestor.element, rect, ancestorRect, ancestorMargin)
      : null;

    if (nextTop === null && nextLeft === null) {
      continue;
    }

    ancestor.element.scrollTo({
      top: nextTop ?? ancestor.element.scrollTop,
      left: nextLeft ?? ancestor.element.scrollLeft,
      behavior,
    });
  }

  const rect = el.getBoundingClientRect();
  const nextWindowTop = getNextWindowScrollTop(
    rect,
    viewportTopMargin,
    viewportBottomMargin,
  );
  const nextWindowLeft = getNextWindowScrollLeft(
    rect,
    viewportLeftMargin,
    viewportRightMargin,
  );

  if (nextWindowTop !== null || nextWindowLeft !== null) {
    window.scrollTo({
      top: nextWindowTop ?? window.scrollY,
      left: nextWindowLeft ?? window.scrollX,
      behavior,
    });
  }
}

function getNextScrollTop(
  container: HTMLElement,
  targetRect: DOMRect,
  containerRect: DOMRect,
  margin: number,
): number | null {
  const minTop = containerRect.top + margin;
  const maxBottom = containerRect.bottom - margin;

  if (targetRect.top < minTop) {
    return Math.max(0, container.scrollTop + targetRect.top - minTop);
  }

  if (targetRect.bottom > maxBottom) {
    return container.scrollTop + targetRect.bottom - maxBottom;
  }

  return null;
}

function getNextScrollLeft(
  container: HTMLElement,
  targetRect: DOMRect,
  containerRect: DOMRect,
  margin: number,
): number | null {
  const minLeft = containerRect.left + margin;
  const maxRight = containerRect.right - margin;

  if (targetRect.left < minLeft) {
    return Math.max(0, container.scrollLeft + targetRect.left - minLeft);
  }

  if (targetRect.right > maxRight) {
    return container.scrollLeft + targetRect.right - maxRight;
  }

  return null;
}

function getNextWindowScrollTop(
  rect: DOMRect,
  topMargin: number,
  bottomMargin: number,
): number | null {
  if (rect.top < topMargin) {
    return Math.max(0, window.scrollY + rect.top - topMargin);
  }

  if (rect.bottom > window.innerHeight - bottomMargin) {
    return Math.max(
      0,
      window.scrollY + rect.bottom - (window.innerHeight - bottomMargin),
    );
  }

  return null;
}

function getNextWindowScrollLeft(
  rect: DOMRect,
  leftMargin: number,
  rightMargin: number,
): number | null {
  if (rect.left < leftMargin) {
    return Math.max(0, window.scrollX + rect.left - leftMargin);
  }

  if (rect.right > window.innerWidth - rightMargin) {
    return Math.max(
      0,
      window.scrollX + rect.right - (window.innerWidth - rightMargin),
    );
  }

  return null;
}
